import moment from 'moment';
import { get } from 'lodash-es';
import dataCommonFormat from '@home/utils/dbDataFormat/dataCommonFormat';

export interface IHoverCompareBubbleConfig {
  currentDateKey?: string;
  currentValueKey?: string;
  compareDateKey?: string;
  compareValueKey?: string;
  valueFormatRule?: string;
  dateFormat?: string;
  dateType?: string;
  timeType?: string;
  changeKey?: string;
  hoverFormatRule?: string;
}

interface IBuildHoverCompareDataParams {
  record: Record<string, unknown>;
  columnCode?: string;
  emptyValue?: string;
  config?: IHoverCompareBubbleConfig;
}

export interface IHoverCompareData {
  currentDate: string;
  currentValue: string;
  compareDate: string;
  compareValue: string;
  titleEn: string;
  titleCn: string;
}

const TITLE = {
  day: {
    titleEn: 'DoD',
    titleCn: '\u65e5\u73af\u6bd4',
  },
  month: {
    titleEn: 'MoM',
    titleCn: '\u6708\u73af\u6bd4',
  },
  change: {
    titleEn: 'Change',
    titleCn: '\u53d8\u5316\u7387',
  },
};

const parseDate = (value: string) => moment(value, ['YYYYMMDD', 'YYYY-MM-DD', 'YYYYMM', 'YYYY-MM'], true);

const isMonthGranularity = (dateFormat?: string) => Boolean(dateFormat?.includes('M') && !dateFormat?.includes('D'));

const getDateFormat = (config?: IHoverCompareBubbleConfig) => {
  if (config?.dateFormat) {
    return config.dateFormat;
  }
  if (config?.timeType) {
    return String(config.timeType).toLowerCase() === 'month' ? 'YYYY-MM' : 'YYYY-MM-DD';
  }
  if (config?.dateType) {
    return String(config.dateType).toLowerCase() === 'monthly' ? 'YYYY-MM' : 'YYYY-MM-DD';
  }
  return 'YYYY-MM-DD';
};

const getUnitFormat = (dateFormat?: string) => (isMonthGranularity(dateFormat) ? 'YYYY-MM' : 'YYYY-MM-DD');


const splitRangeDates = (value: unknown) => String(value ?? '')
  .split(/[~\uFF5E]/)
  .map(item => item.trim())
  .filter(Boolean);

const formatSingleDate = (value: string, dateFormat: string) => {
  const parsed = parseDate(value);
  const fallbackParsed = moment(value);
  if (parsed.isValid()) {
    return parsed.format(dateFormat);
  }
  if (fallbackParsed.isValid()) {
    return fallbackParsed.format(dateFormat);
  }
  return value;
};

const formatDate = (value: unknown, dateFormat?: string, emptyValue = '-') => {
  if (value === undefined || value === null || value === '') {
    return emptyValue;
  }

  if (!dateFormat) {
    return String(value);
  }

  const raw = String(value);
  const rangeDates = splitRangeDates(raw);
  if (rangeDates.length >= 2) {
    const unitFormat = getUnitFormat(dateFormat);
    const start = formatSingleDate(rangeDates[0], unitFormat);
    const end = formatSingleDate(rangeDates[1], unitFormat);

    return start === end ? start : `${start}~${end}`;
  }

  return formatSingleDate(rangeDates[0] || raw, dateFormat);
};

const getCompareTitle = (value: unknown, dateFormat?: string) => {
  const defaultTitle = isMonthGranularity(dateFormat) ? TITLE.month : TITLE.day;
  const rangeDates = splitRangeDates(value);
  if (rangeDates.length < 2) {
    return defaultTitle;
  }

  const unitFormat = getUnitFormat(dateFormat);
  const start = formatSingleDate(rangeDates[0], unitFormat);
  const end = formatSingleDate(rangeDates[1], unitFormat);

  return start === end ? defaultTitle : TITLE.change;
};

const formatValue = (value: unknown, valueFormatRule?: string, emptyValue = '-') => {
  if (!valueFormatRule) {
    return String(value ?? emptyValue);
  }

  return String(dataCommonFormat({
    value,
    formatRule: valueFormatRule,
    emptyValue,
  }) ?? emptyValue);
};

export const buildHoverCompareData = ({
  record,
  columnCode = '',
  emptyValue = '-',
  config,
}: IBuildHoverCompareDataParams): IHoverCompareData => {
  const currentValueKey = config?.currentValueKey || columnCode;
  const compareDateKey = config?.compareDateKey || (config?.changeKey ? `${config.changeKey}_date` : undefined);
  const compareValueKey = config?.compareValueKey || (config?.changeKey ? `${config.changeKey}_value` : undefined);
  const valueFormatRule = config?.valueFormatRule || config?.hoverFormatRule;
  const dateFormat = getDateFormat(config);
  const currentDateValue = (config?.currentDateKey ? get(record, config.currentDateKey) : undefined)
    ?? get(record, 'date');

  return {
    ...getCompareTitle(currentDateValue, dateFormat),
    currentDate: formatDate(
      currentDateValue,
      dateFormat,
      emptyValue,
    ),
    currentValue: formatValue(
      currentValueKey ? get(record, currentValueKey) : undefined,
      valueFormatRule,
      emptyValue,
    ),
    compareDate: formatDate(
      compareDateKey ? get(record, compareDateKey) : undefined,
      dateFormat,
      emptyValue,
    ),
    compareValue: formatValue(
      compareValueKey ? get(record, compareValueKey) : undefined,
      valueFormatRule,
      emptyValue,
    ),
  };
};
